import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { SignInForm } from 'components/auth/forms/SignInForm';
import { getAuthToken } from 'components/auth/getAuthToken';
import { MarketingLayout } from 'components/auth/MarketingLayout';
import * as routes from 'config/routes';
import { signIn } from 'store/auth/authSlice';
import { Link } from 'ui/Link';

const SignInPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { error, claims } = useAppSelector(state => state.auth);

    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [redirectTo, setRedirectTo] = React.useState<string | null>(null);

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const redirectParam = params.get('redirectTo');
        setRedirectTo(redirectParam);
        const checkAuth = async () => {
            const authToken = await getAuthToken();
            if (authToken && !error) {
                navigate(redirectParam || routes.USER_MERCHANTS, { replace: true });
            }
        };
        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const handleUpdate = async () => {
            if (isSubmitted && (error || claims)) {
                if (claims) {
                    navigate(redirectTo || routes.USER_MERCHANTS);
                } else {
                    setIsSubmitted(false);
                }
            } else if (claims) {
                const authToken = await getAuthToken();
                if (authToken && !error) {
                    navigate(redirectTo || routes.USER_MERCHANTS, { replace: true });
                }
            }
        };
        handleUpdate();
    }, [claims, error, isSubmitted, navigate, redirectTo]);

    const handleSubmit = React.useCallback(
        async (data: { email: string; password: string }) => {
            const { email, password } = data;
            await dispatch(signIn(email, password));
            setIsSubmitted(true);
        },
        [dispatch]
    );

    const handleSignUpClick = React.useCallback(
        (e: React.MouseEvent) => {
            if (redirectTo) {
                e.preventDefault();
                navigate(`${routes.SIGN_UP}?redirectTo=${redirectTo}`);
            }
        },
        [navigate, redirectTo]
    );

    return (
        <MarketingLayout
            marketingId="optimize-sign-in"
            after={
                <React.Fragment>
                    <Typography align="center" variant="caption" color="textSecondary">
                        <Link href={routes.FORGOT_YOUR_PASSWORD}>Forgot your password? </Link>
                    </Typography>
                    <Typography align="center" variant="caption" color="textSecondary">
                        Don&apos;t have an account?{' '}
                        <Link onClick={handleSignUpClick} href={routes.SIGN_UP}>
                            Sign up
                        </Link>
                    </Typography>
                </React.Fragment>
            }
        >
            <SignInForm onSubmit={handleSubmit} />
            {error && (
                <Typography sx={{ pt: 2 }} align="center" color="error" data-cy="loginErrorLabel">
                    {error}
                </Typography>
            )}
        </MarketingLayout>
    );
};

export default SignInPage;
